import { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { 
  Users, Trophy, Calendar, ArrowLeft, CheckCircle, Clock, 
  MapPin, Play, Timer, ChevronRight, Home, Plane, Facebook
} from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import Layout from "@/components/layout/Layout";
import { AdBanner } from "@/components/shared/AdPlaceholder";
import axios from "axios";
import { API } from "@/App";
import { getImageUrl } from "@/utils/imageUrl";

const formatDate = (dateStr) => {
  if (!dateStr) return "";
  const date = new Date(dateStr);
  return date.toLocaleDateString("es-HN", { weekday: "short", day: "numeric", month: "short" });
};

const STATUS_LABELS = {
  scheduled: { label: "Programado", color: "bg-blue-100 text-blue-700" },
  live: { label: "En Vivo", color: "bg-red-100 text-red-700" },
  finished: { label: "Finalizado", color: "bg-green-100 text-green-700" },
  suspended: { label: "Suspendido", color: "bg-yellow-100 text-yellow-700" },
  postponed: { label: "Aplazado", color: "bg-gray-100 text-gray-700" },
};

// Match Card for Team
function TeamMatchCard({ match, teamId }) {
  const isHome = match.home_team_id === teamId || match.is_home;
  const opponent = match.opponent || (isHome ? match.away_team : match.home_team);
  const statusInfo = STATUS_LABELS[match.status] || STATUS_LABELS.scheduled;
  
  let result = null;
  if (match.status === "finished") {
    const teamScore = isHome ? match.home_score : match.away_score;
    const opponentScore = isHome ? match.away_score : match.home_score;
    if (teamScore > opponentScore) result = { label: "V", color: "bg-green-500" };
    else if (teamScore < opponentScore) result = { label: "D", color: "bg-red-500" };
    else result = { label: "E", color: "bg-yellow-500" };
  }

  return (
    <div className="flex items-center gap-4 p-4 bg-white rounded-xl border hover:shadow-md transition-shadow">
      <div className="flex items-center gap-3 flex-1">
        {/* Result Badge */}
        {result && (
          <span className={`w-8 h-8 rounded-full ${result.color} text-white font-bold flex items-center justify-center text-sm`}>
            {result.label}
          </span>
        )}
        
        {/* Home/Away indicator */}
        <span className="text-xs text-gray-400">
          {isHome ? <Home className="h-4 w-4" /> : <Plane className="h-4 w-4" />}
        </span>
        
        {/* Opponent */}
        <div className="flex items-center gap-2">
          {opponent?.logo_url && (
            <img 
              src={getImageUrl(opponent.logo_url)} 
              alt={opponent.name}
              className="w-8 h-8 object-contain"
            />
          )}
          <span className="font-medium">{isHome ? "vs" : "@"} {opponent?.name || "Oponente"}</span>
        </div>
      </div>
      
      {/* Score */}
      {match.status === "finished" || match.status === "live" ? (
        <div className="font-bold text-lg">
          {isHome ? `${match.home_score}-${match.away_score}` : `${match.away_score}-${match.home_score}`}
        </div>
      ) : (
        <span className="text-gray-500 text-sm">{match.match_time || "TBD"}</span>
      )}
      
      {/* Date & Status */}
      <div className="text-right">
        <Badge className={`${statusInfo.color} text-xs`}>{statusInfo.label}</Badge>
        <p className="text-xs text-gray-400 mt-1">{formatDate(match.match_date)}</p>
      </div>
    </div>
  );
}

export default function EquipoPage() {
  const { slug } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchTeamData();
  }, [slug]);

  const fetchTeamData = async () => {
    try {
      const response = await axios.get(`${API}/sports/teams/${slug}/full`);
      setData(response.data);
    } catch (error) {
      console.error("Error fetching team data:", error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="min-h-[60vh] flex items-center justify-center">
          <div className="animate-spin rounded-full h-12 w-12 border-4 border-primary border-t-transparent"></div>
        </div>
      </Layout>
    );
  }

  if (!data || !data.team) {
    return (
      <Layout>
        <div className="min-h-[60vh] flex flex-col items-center justify-center">
          <Users className="h-16 w-16 text-gray-300 mb-4" />
          <h2 className="text-xl font-medium text-gray-600 mb-2">Equipo no encontrado</h2>
          <Link to="/deportes">
            <Button variant="outline" className="mt-4">
              <ArrowLeft className="h-4 w-4 mr-2" />
              Volver a Deportes
            </Button>
          </Link>
        </div>
      </Layout>
    );
  }

  const { team, recent_matches, upcoming_matches, tournaments } = data;

  return (
    <Layout>
      {/* Hero */}
      <section className="relative py-16 md:py-24 bg-gradient-to-br from-gray-900 to-gray-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
          <Link 
            to="/deportes" 
            className="inline-flex items-center gap-2 text-white/80 hover:text-white mb-6 transition-colors"
          >
            <ArrowLeft className="h-4 w-4" />
            Volver a Deportes
          </Link>
          
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="flex flex-col md:flex-row items-center gap-8"
          >
            {team.logo_url ? (
              <img 
                src={getImageUrl(team.logo_url)} 
                alt={team.name}
                className="w-32 h-32 md:w-40 md:h-40 object-contain bg-white rounded-2xl p-4"
              />
            ) : (
              <div className="w-32 h-32 md:w-40 md:h-40 bg-white/10 rounded-2xl flex items-center justify-center">
                <Users className="h-16 w-16 text-white/50" />
              </div>
            )}
            
            <div className="text-center md:text-left">
              <h1 className="font-heading text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-2">
                {team.name}
              </h1>
              {team.city && (
                <p className="text-white/80 text-lg flex items-center justify-center md:justify-start gap-2 mb-4">
                  <MapPin className="h-5 w-5" />
                  {team.city}, {team.country || "Honduras"}
                </p>
              )}
              <div className="flex flex-wrap items-center justify-center md:justify-start gap-3">
                {team.short_name && (
                  <Badge className="bg-white/20 text-white border-0">{team.short_name}</Badge>
                )}
                {team.facebook_url && (
                  <a 
                    href={team.facebook_url} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="flex items-center gap-2 px-3 py-1.5 bg-blue-600 hover:bg-blue-700 text-white rounded-full text-sm font-medium transition-colors"
                    title="Página oficial en Facebook"
                  >
                    <Facebook className="h-4 w-4" />
                    Facebook
                  </a>
                )}
                {tournaments.map((t) => (
                  <Link key={t.tournament_id} to={`/deportes/torneo/${t.slug}`}>
                    <Badge 
                      className="text-white border-0"
                      style={{ backgroundColor: t.color || "#10B981" }}
                    >
                      <Trophy className="h-3 w-3 mr-1" />
                      {t.name}
                      {t.team_position && ` - #${t.team_position}`}
                    </Badge>
                  </Link>
                ))}
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Stats Summary */}
      {tournaments.length > 0 && tournaments[0].team_stats && (
        <section className="py-8 bg-white border-b">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-8 gap-4 text-center">
              {[
                { label: "Posición", value: tournaments[0].team_position || "-" },
                { label: "PJ", value: tournaments[0].team_stats.played },
                { label: "G", value: tournaments[0].team_stats.won, color: "text-green-600" },
                { label: "E", value: tournaments[0].team_stats.drawn, color: "text-yellow-600" },
                { label: "P", value: tournaments[0].team_stats.lost, color: "text-red-600" },
                { label: "GF", value: tournaments[0].team_stats.goals_for },
                { label: "GC", value: tournaments[0].team_stats.goals_against },
                { label: "PTS", value: tournaments[0].team_stats.points, color: "text-primary font-bold" },
              ].map((stat) => (
                <div key={stat.label} className="bg-gray-50 rounded-xl p-4">
                  <p className="text-xs text-gray-500 uppercase">{stat.label}</p>
                  <p className={`text-2xl font-bold ${stat.color || "text-gray-800"}`}>{stat.value}</p>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Content */}
      <section className="py-12 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Tabs defaultValue="recent" className="space-y-6">
            <TabsList className="grid w-full max-w-md grid-cols-2">
              <TabsTrigger value="recent">Resultados</TabsTrigger>
              <TabsTrigger value="upcoming">Próximos</TabsTrigger>
            </TabsList>

            {/* Recent Matches */}
            <TabsContent value="recent">
              <div className="bg-white rounded-2xl p-6">
                <h2 className="font-heading text-xl font-bold text-brand-text mb-6 flex items-center gap-2">
                  <CheckCircle className="h-5 w-5 text-green-500" />
                  Últimos Resultados
                </h2>
                
                {recent_matches && recent_matches.length > 0 ? (
                  <div className="space-y-3">
                    {recent_matches.map((match) => (
                      <TeamMatchCard key={match.match_id} match={match} teamId={team.team_id} />
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-8 text-gray-500">
                    No hay resultados recientes
                  </div>
                )}
              </div>
            </TabsContent>

            {/* Upcoming Matches */}
            <TabsContent value="upcoming">
              <div className="bg-white rounded-2xl p-6">
                <h2 className="font-heading text-xl font-bold text-brand-text mb-6 flex items-center gap-2">
                  <Calendar className="h-5 w-5 text-blue-500" />
                  Próximos Partidos
                </h2>
                
                {upcoming_matches && upcoming_matches.length > 0 ? (
                  <div className="space-y-3">
                    {upcoming_matches.map((match) => (
                      <TeamMatchCard key={match.match_id} match={match} teamId={team.team_id} />
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-8 text-gray-500">
                    No hay partidos programados
                  </div>
                )}
              </div>
            </TabsContent>
          </Tabs>
        </div>
      </section>

      {/* Ad Banner */}
      <section className="py-8 bg-gray-50">
        <AdBanner />
      </section>
    </Layout>
  );
}
